import Config from 'react-native-config';
import * as DateFns from 'date-fns';
import {startOfDay} from 'date-fns';
import {formatInTimeZone, utcToZonedTime} from 'date-fns-tz';
import {enUS, es} from 'date-fns/locale';
import {isDate, isEmpty, isNil} from 'lodash/fp';

import {MS_IN_DAY, MS_IN_MINUTE} from '/constant/time.constant';
import {REPORT_ALL_CANDIDATES} from '/enum/records.enum';
// Import directly from TranslationProvider/withTranslation to avoid circular dependencies
import withTranslation from '/provider/TranslationProvider/withTranslation';
import {getErrorTrackingUtilDefaultInstance} from '/util/error-tracking/error-tracking.util';

import {Logger} from './splunk/logger.util';

const SUPPORTED_LOCALES = {
  en: enUS,
  es: es,
};

const RELATIVE_OPTION = {
  [0]: {key: 'dates.today', defaultValue: 'today'},
  [1]: {key: 'dates.yesterday', defaultValue: 'yesterday'},
  [2]: {key: 'dates.twoDaysAgo', defaultValue: 'two days ago'},
  [-1]: {key: 'dates.tomorrow', defaultValue: 'tomorrow'},
};

// This class must use `withTranslation.ts` (instead of `tt`) because
// functions in this class can only return translated strings and not
// translation templates because the return value of these functions
// are often used inside other translation templates.

export class DateUtil {
  private locale: Locale | null;

  constructor() {
    this.locale = null;
  }

  setLocale(locale: keyof typeof SUPPORTED_LOCALES) {
    this.locale = SUPPORTED_LOCALES[locale] || SUPPORTED_LOCALES.en;
  }

  formatDate(date: any, formatStr: any) {
    try {
      const formatArgs = [formatStr, {locale: this.locale}];
      if (isDate(date)) {
        // @ts-expect-error TS(2556): A spread argument must either have a tuple type or... Remove this comment to see the full error message
        return DateFns.format(date, ...formatArgs);
      } else if (!isEmpty(date)) {
        // @ts-expect-error TS(2556): A spread argument must either have a tuple type or... Remove this comment to see the full error message
        return DateFns.format(DateFns.parseISO(date), ...formatArgs);
      } else {
        return null;
      }
    } catch (error) {
      getErrorTrackingUtilDefaultInstance().trackError(
        error,
        'Date format error',
      );
      return null;
    }
  }

  parseDate(date: string, formatStr: string) {
    try {
      return DateFns.parse(date, formatStr, new Date());
    } catch (error) {
      getErrorTrackingUtilDefaultInstance().trackError(
        error,
        'Parse Date  error',
      );
      return null;
    }
  }

  formatRelativeDate(
    date: string | number | Date,
    longFormatStr: string,
  ): string | null {
    const daysToToday = this.getDaysToToday(date);
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const relativeOption = RELATIVE_OPTION[daysToToday];
    if (!isNil(relativeOption)) {
      const {key, defaultValue} = relativeOption;
      return withTranslation.ts(key, defaultValue);
    } else {
      // Because `formatDate` returns an already translated date string,
      // it's fine for us to use that value as a string and not a variable
      // inside a translation template.
      return this.formatDate(date, longFormatStr);
    }
  }

  isDateTodayOrPast(date: string | number | Date): boolean {
    return this.getDaysToToday(date) >= 0;
  }

  isDateWithinRelativeRange(date: string | number | Date): boolean {
    const daysToToday = this.getDaysToToday(date);
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const relativeOption = RELATIVE_OPTION[daysToToday];
    return !isNil(relativeOption);
  }

  showDate(date: Date): string {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const day = date.getDate();

    return `${month}-${day}-${year}`;
  }

  getDaysToToday(date: any) {
    const nowTime = new Date(this.getNow());
    const dateTime = new Date(this.getTimeFromDateString(date));
    const nowDate = startOfDay(nowTime);
    const TodayDate = startOfDay(dateTime);
    return DateFns.differenceInDays(nowDate, TodayDate);
  }

  /**
   * Returns the number of day(s) between two dates.
   * It returns a negative number if startDate is past the destDate.
   * @param {String} startDate - date string
   * @param {String} destDate - date string
   * @returns {number} returns the day(s) difference.
   */
  getTimePassed(startDate: string, destDate: string): number {
    let inputDate = new Date(DateFns.parseISO(startDate).getTime());
    const now = destDate
      ? new Date(DateFns.parseISO(destDate).getTime())
      : Date.parse(this.getESTLocaleString());
    // @ts-expect-error TS(2362): The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
    const daysDiff = now - inputDate;
    return daysDiff / MS_IN_DAY;
  }

  getUTCOffset(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
      ),
    );
  }

  /**
   * Gets date without UTC timezone code and returns the same date with UTC code.
   * @param {dateUTC} string Date in UTC that doesn't have UTC timezone code
   * @returns {Date} UTC date in correct format
   */
  formatUTC(dateUTC: string | number | Date): Date {
    let date = new Date(dateUTC);
    let newDate = new Date(
      date.getTime() + date.getTimezoneOffset() * 60 * 1000,
    );

    let offset = date.getTimezoneOffset() / 60;
    let hours = date.getHours();

    newDate.setHours(hours - offset);

    return newDate;
  }

  /**
   * Returns true if the current date is between the date interval entered.
   * @returns {Boolean} returns true if current Date is within interval
   * @param startDate - date in format 'YYYY-MM-DD HH:MM:SS' in UTC
   * @param endDate - date in format 'YYYY-MM-DD HH:MM:SS' in UTC
   */
  isDateWithinRange(
    startDate: string | number | Date,
    endDate: string | number | Date,
  ): boolean {
    try {
      const startDateTime = new Date(this.formatUTC(startDate));
      const endDateTime = new Date(this.formatUTC(endDate));
      const todayDateTime = new Date(this.getNow());

      const startDateWithUTCOffset = this.getUTCOffset(startDateTime);
      const endDateWithUTCOffset = this.getUTCOffset(endDateTime);
      const todayDateWithUTCOffset = this.getUTCOffset(todayDateTime);

      return DateFns.isWithinInterval(todayDateWithUTCOffset, {
        start: startDateWithUTCOffset,
        end: endDateWithUTCOffset,
      });
    } catch (err) {
      getErrorTrackingUtilDefaultInstance().trackError(
        err,
        'Date format error',
      );
      return false;
    }
  }

  /**
   * Returns true if the current date is on or before the date entered.
   * @param {endDate} string Date in UTC timezone
   * @returns {Boolean} returns true if current Date is equal or before the given date
   */
  isDateSameOrBefore(endDate: string | number | Date): boolean {
    const endDateTime = this.formatUTC(endDate);
    const todayDateTime = new Date(this.getNow());
    const endDateWithUTCOffset = endDateTime.getTime();
    const todayDateWithUTCOffset = todayDateTime.getTime();
    return todayDateWithUTCOffset <= endDateWithUTCOffset;
  }

  /**
   * Returns true if the current date is on or after the date entered.
   * @param {startDate} string Date in UTC timezone
   * @returns {Boolean} returns true if current Date is equal or after the given date
   */

  isDateSameOrAfter(startDate: string | number | Date): boolean {
    const startDateTime = this.formatUTC(startDate);
    const todayDateTime = new Date(this.getNow());
    const startDateWithUTCOffset = startDateTime.getTime();
    const todayDateWithUTCOffset = todayDateTime.getTime();
    return todayDateWithUTCOffset >= startDateWithUTCOffset;
  }

  /**
   * Detects whether a date string in 'YYYY-MM-DD HH:mm:ss' falls under US Daylight Saving Time.
   */
  isDSTForEastern(dateStr: string): boolean {
    // Create a date using EST as base (UTC-5)
    const date = new Date(dateStr.replace(' ', 'T') + '-05:00');
    const janOffset = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    return date.getTimezoneOffset() < janOffset;
  }

  /**
   * Parses a date string (Eastern Time) into a JS Date object that respects DST (EST/EDT).
   * @param dateStr - Example: '2024-04-22 03:09:09'
   */
  parseEasternDate(dateStr: string): Date {
    const iso = dateStr.replace(' ', 'T');
    const isDST = this.isDSTForEastern(dateStr);
    const offset = isDST ? '-04:00' : '-05:00';
    return new Date(`${iso}${offset}`);
  }

  /**
   * Returns true if the given eastern order date is on or after the local cutoff date.
   * Premier+ cutoff date  '2025-07-08T00:00:00',
   */
  isPremierPlusDateReached(
    orderedOnEastern: string,
    localCutoffDateStr: string = '2025-07-08T00:00:00',
  ): boolean {
    const orderedDate = this.parseEasternDate(orderedOnEastern);
    const cutoffDate = new Date(localCutoffDateStr);
    return orderedDate.getTime() >= cutoffDate.getTime();
  }

  /**
   * Returns the difference for provided date and current time.
   * @param {Number|Date} date The date in the past (less than current date)
   * @param {Number} divider The date divider, one minute by default
   * @returns {Number} returns a unit (minute, second, hour) difference
   */
  getDifference(date: number, divider: number = MS_IN_MINUTE): number {
    const currTime = this.getNow();
    // @ts-expect-error TS(2362): The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
    const msSinceDate = currTime - date;
    return Math.floor(msSinceDate / divider);
  }

  getNow() {
    return Config.QA_DATE ? new Date(Config.QA_DATE) : new Date();
  }

  getEpoch(time: string | number | Date): number {
    return new Date(time).getTime() / 1000;
  }

  getESTLocaleString(): string {
    return this.getNow().toLocaleString('en-US', {
      timeZone: 'America/New_York',
    });
  }

  isDateEqualOrBefore(
    startDate: string | number | Date,
    endDate: string | number | Date,
  ): boolean {
    // startDate received from api as EST format and converting it to UTC format
    // added 5hour timezone offset considering the day light savings timings
    const startDateTimeUTC = new Date(`${startDate}-04:00`);
    // endDate should be entered in UTC format and adding UTC timezone offset to endDate
    const endDateTimeUTC = this.formatUTC(endDate);

    return (
      DateFns.differenceInMilliseconds(startDateTimeUTC, endDateTimeUTC) <= 0
    );
  }

  isWithinInterval(
    currentDate: string | number | Date,
    startDate: string | number | Date,
    endDate: string | number | Date,
  ): boolean {
    const startDateTime = new Date(this.formatUTC(startDate));
    const endDateTime = new Date(this.formatUTC(endDate));
    const currentDateTime = new Date(this.formatUTC(currentDate));

    return DateFns.isWithinInterval(currentDateTime, {
      start: startDateTime,
      end: endDateTime,
    });
  }

  /**
   * Parses a CST/CDT formatted date string and converts it to UTC.
   * @param {string} dateStr - The date string in CST/CDT format (e.g., '2025-04-01 00:00:00').
   * @returns {Date} - The date object converted to UTC.
   */
  parseCSTorCDTDate = (dateStr: string): Date => {
    // Ensure dateStr is a string before attempting to use replace
    if (typeof dateStr !== 'string') {
      throw new TypeError('dateStr must be a string');
    }
    // Replace space with 'T' to ensure correct ISO format
    const formattedDateStr = dateStr.includes(' ')
      ? dateStr.replace(' ', 'T')
      : dateStr;
    // Create Date object (assumes input is in local timezone, typically CST/CDT)
    const localDate = new Date(formattedDateStr);

    // Convert local CST/CDT time to UTC
    return new Date(
      localDate.getTime() + localDate.getTimezoneOffset() * 60 * 1000,
    );
  };

  /**
   * Checks if a given date is within a two-month period starting from the DDB end date.
   * The end date is exclusive of June 1st.
   * @param {string} DDBEndDate - The end date in CST/CDT format (e.g., '2025-04-01 00:00:00').
   * @param {string} dateToCheck - The date to check in CST/CDT format.
   * @returns {boolean} - Returns true if the dateToCheck is within the two-month period, false otherwise.
   */
  isDateWithinTwoMonthsPeriod = (
    DDBEndDate: string | null,
    dateToCheck: string | null,
  ): boolean => {
    if (!DDBEndDate || !dateToCheck) return false; // Return false if either date is null

    const startDate = this.parseCSTorCDTDate(DDBEndDate);
    if (isNaN(startDate.getTime())) return false; // Invalid start date

    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + 2); // Add 2 months
    endDate.setUTCHours(0, 0, 0, 0);

    const checkDateUTC = this.parseCSTorCDTDate(dateToCheck);
    if (isNaN(checkDateUTC.getTime())) return false; // Invalid check date

    return checkDateUTC >= startDate && checkDateUTC < endDate;
  };

  /**
   * Gets the last day of the previous month based on the given date.
   * @param {string} dateString - The reference date in ISO format.
   * @returns {string} Returns the last day of the previous month in ISO format (UTC time).
   */
  getLastDayOfPreviousMonth = (dateString: string | null): string => {
    if (!dateString || typeof dateString !== 'string') return ''; // Return empty string for null values

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ''; // Return empty string for invalid dates

    date.setUTCMonth(date.getUTCMonth(), 0); // Move to the last day of the previous month
    date.setUTCHours(0, 0, 0, 0);
    return date.toISOString();
  };

  getMonthNames(
    startDate: string | number | Date,
    endDate: string | number | Date,
    lang = 'en',
  ): string[] {
    // startDate , endDate received from api as EST format and converting it to UTC format
    const startDateUTC = new Date(`${startDate}-04:00`);
    const endDateUTC = new Date(`${endDate}-04:00`);
    let stDate = new Date(startDateUTC.getFullYear(), startDateUTC.getMonth());
    let months: string[] = [];
    while (stDate <= endDateUTC) {
      months.push(stDate.toLocaleString(lang, {month: 'short'}));
      stDate.setMonth(stDate.getMonth() + 1);
    }
    return months;
  }

  /**
   * Returns localtime in hours.
   * @param {date} string Date string in format 'YYYY-MM-DD HH:MM:SS'
   * @returns {string} returns the local time in hours
   */

  getLocalTimeInHours(date: string): string {
    try {
      const dateTimeInCST = formatInTimeZone(
        date,
        'America/Chicago',
        'yyyy-MM-dd HH:mm:ssXXX',
      );
      const cstDateObj = new Date(dateTimeInCST);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const localTime = utcToZonedTime(cstDateObj, userTimezone);
      return DateFns.format(localTime, 'hh:mm a');
    } catch (err) {
      Logger.requestSplunkLog({
        logType: 'Error',
        message: `Error in date.util getLocalTimeInHours : ${err}`,
        severity: 'LOW',
      });
      getErrorTrackingUtilDefaultInstance().trackError(
        err,
        'Date format error',
      );
      return 'invalid date';
    }
  }

  getTimeFromDateString = (dateTime: string) => {
    return isDate(dateTime) ? dateTime.getTime() : DateFns.parseISO(dateTime);
  };

  checkDate = (dateString: string, reportName: string) => {
    const givenDate: Date = new Date(dateString);
    const now: Date =
      reportName === REPORT_ALL_CANDIDATES
        ? new Date()
        : new Date(this.getNow());
    const tomorrow: Date = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (givenDate.toDateString() === now.toDateString()) {
      return 'today';
    } else if (givenDate.toDateString() === tomorrow.toDateString()) {
      return 'tomorrow';
    } else {
      return '';
    }
  };

  getUserTimezone = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const acceptedTimezones = [
      'America/New_York',
      'America/Los_Angeles',
      'America/Chicago',
      'America/Denver',
    ];

    // Return the timezone if it is in the accepted list; otherwise, default to 'America/New_York'
    return acceptedTimezones.includes(timezone) ? timezone : 'America/New_York';
  };
}

let defaultInstance: DateUtil | null = null;

export const getDateUtilDefaultInstance = (): DateUtil => {
  if (defaultInstance === null) {
    defaultInstance = new DateUtil();
  }
  return defaultInstance;
};

export const getDateISOStringWithoutTimezoneWithMill = (date: Date): string => {
  return `${new Date(date.getTime()).toISOString().slice(0, 23)}Z`;
};

export const formatTimeByOffset = (dateString: any) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!dateString) return '';
  if (dateString.length === 0) return '';

  const year = dateString.slice(0, 4);
  const month = dateString.slice(5, 7);
  const day = dateString.slice(8, 10);
  const hour = dateString.slice(11, 13);
  const minute = dateString.slice(14, 16);
  const second = dateString.slice(17, 19);

  const dateObject = new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second}Z`,
  );
  const options: Intl.DateTimeFormatOptions = {
    timeZone: userTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(dateObject);

  const formattedDate = parts
    .map(part => part.value)
    .join('')
    .replace(',', '')
    .replace(' at ', ' ')
    .trim();

  const formattedDateParts = formattedDate.split(' ');
  const dateOnly = formattedDateParts[0];

  if (!dateOnly) return '';

  const [monthPart, dayPart, yearPart] = dateOnly.split('/');

  if (!monthPart || !dayPart || !yearPart) return '';

  const finalFormattedDate = `${yearPart}-${monthPart}-${dayPart}`;

  return finalFormattedDate.slice(0, 16);
};
